/**
 * PlatformSettingsForm — extracted from the legacy `AdminPortal.jsx`
 * "Platform Settings" tab during Phase 1 of the App Shell Redesign.
 *
 * Self-contained: owns its own form state, hydration from ConfigContext,
 * validation, save/reset dialog, "last updated" stamp. NO behavior change
 * vs the legacy implementation — same fields, same validators, same
 * server contract. The only structural difference is that the form now
 * lives at `/admin/settings` (where it's stacked above SecuritySettings)
 * instead of inside the legacy two-axis tab/sidebar layout.
 *
 * The 7 fields (default_per_dog_price, default_recurring_discount_pct,
 * default_commission_pct, securement_fee, provider_display_label,
 * credit_label, admin_portal_heading) are unchanged. `admin_portal_heading`
 * is the Option D template (default `{firstName}'s controls`) — see
 * AdminShell.jsx for the interpolation at render time.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { cn } from "@/lib/utils";
import { Loader2, RotateCcw } from "lucide-react";

const CANONICAL_DEFAULTS = {
  default_per_dog_price: 20,
  default_recurring_discount_pct: 10,
  default_commission_pct: 10,
  securement_fee: 15,
  provider_display_label: "PooPartner",
  credit_label: "PooBucks",
};

function validateField(name, raw) {
  const v = typeof raw === "string" ? raw.trim() : raw;
  if (name === "provider_display_label" || name === "credit_label") {
    if (!v) return "Required.";
    if (v.length > 30) return "30 characters max.";
    return null;
  }
  if (name === "admin_portal_heading") {
    if (!v) return "Required.";
    if (v.length > 60) return "60 characters max.";
    return null;
  }
  const n = parseFloat(v);
  if (v === "" || Number.isNaN(n)) return "Required.";
  if (n < 0) return "Must be 0 or more.";
  if (
    (name === "default_recurring_discount_pct" || name === "default_commission_pct") &&
    n > 100
  ) {
    return "Must be 0–100.";
  }
  return null;
}

const FIELDS = [
  {
    key: "default_per_dog_price",
    label: "Default $ / Dog",
    type: "currency",
    helper: "Platform-wide per-dog price applied to every booking.",
    testid: "admin-field-per-dog",
  },
  {
    key: "default_recurring_discount_pct",
    label: "Default Recurring Discount %",
    type: "percent",
    helper: "Percentage off for weekly / twice-weekly recurring bookings.",
    testid: "admin-field-recurring",
  },
  {
    key: "default_commission_pct",
    label: "Platform Fee %",
    type: "percent",
    helper: "PooPluckers' cut of every booking. PooPartners keep the rest.",
    testid: "admin-field-commission",
  },
  {
    key: "securement_fee",
    label: "Securement Fee (per dog)",
    type: "currency",
    helper: "Added per dog that isn't secured (kennel/inside) during cleaning.",
    testid: "admin-field-securement",
  },
  {
    key: "provider_display_label",
    label: "PooPartner Display Label",
    type: "text",
    helper: "What we call PooPartners across the site. Saves and updates everywhere.",
    testid: "admin-field-label",
  },
  {
    key: "credit_label",
    label: "Credit Display Label",
    type: "text",
    helper: "What we call account credits across the site (e.g. PooBucks, Credits).",
    testid: "admin-field-credit-label",
  },
  {
    key: "admin_portal_heading",
    label: "Admin portal heading",
    type: "text",
    helper:
      "Heading shown at the top of the admin portal. Use {firstName} to " +
      "interpolate the logged-in admin's first name. Remove the token for " +
      "a fixed string (e.g. \"PooPlucker HQ\" displays to everyone).",
    testid: "admin-field-portal-heading",
  },
];

function PrefixedInput({ field, value, onChange, error }) {
  const isCurrency = field.type === "currency";
  const isPercent = field.type === "percent";
  const isText = field.type === "text";
  return (
    <div className="relative">
      {isCurrency && (
        <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
          $
        </span>
      )}
      <Input
        type={isText ? "text" : "number"}
        step={isCurrency ? "0.01" : "0.1"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-12 rounded-full px-5",
          isCurrency && "pl-9",
          isPercent && "pr-9",
          error && "border-red-500 focus-visible:ring-red-500"
        )}
        data-testid={`${field.testid}-input`}
      />
      {isPercent && (
        <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
          %
        </span>
      )}
    </div>
  );
}

export default function PlatformSettingsForm() {
  const { pricing, refresh } = useConfig();
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    if (!pricing) return;
    setForm({
      default_per_dog_price: String(pricing.default_per_dog_price ?? ""),
      default_recurring_discount_pct: String(pricing.default_recurring_discount_pct ?? ""),
      default_commission_pct: String(pricing.default_commission_pct ?? ""),
      securement_fee: String(pricing.securement_fee ?? ""),
      provider_display_label: pricing.provider_display_label ?? "PooPartner",
      credit_label: pricing.credit_label ?? "PooBucks",
      admin_portal_heading: pricing.admin_portal_heading ?? "{firstName}'s controls",
    });
    setUpdatedAt(pricing.updated_at || null);
  }, [pricing]);

  const dirty = useMemo(() => {
    if (!form || !pricing) return false;
    return (
      String(pricing.default_per_dog_price) !== form.default_per_dog_price ||
      String(pricing.default_recurring_discount_pct) !== form.default_recurring_discount_pct ||
      String(pricing.default_commission_pct) !== form.default_commission_pct ||
      String(pricing.securement_fee) !== form.securement_fee ||
      pricing.provider_display_label !== form.provider_display_label.trim() ||
      (pricing.credit_label || "PooBucks") !== form.credit_label.trim() ||
      (pricing.admin_portal_heading || "{firstName}'s controls") !==
        (form.admin_portal_heading || "").trim()
    );
  }, [form, pricing]);

  const hasErrors = useMemo(
    () => Object.values(errors).some(Boolean),
    [errors]
  );

  const update = (k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
    setErrors((s) => ({ ...s, [k]: validateField(k, v) }));
  };

  const validateAll = () => {
    const next = {};
    FIELDS.forEach((f) => {
      next[f.key] = validateField(f.key, form[f.key]);
    });
    setErrors(next);
    return Object.values(next).every((e) => !e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) return;
    setSaving(true);
    try {
      const payload = {
        default_per_dog_price: parseFloat(form.default_per_dog_price),
        default_recurring_discount_pct: parseFloat(form.default_recurring_discount_pct),
        default_commission_pct: parseFloat(form.default_commission_pct),
        securement_fee: parseFloat(form.securement_fee),
        provider_display_label: form.provider_display_label.trim(),
        credit_label: form.credit_label.trim(),
        admin_portal_heading: (form.admin_portal_heading || "").trim(),
      };
      const { data } = await api.patch("/config/pricing", payload);
      setForm({
        default_per_dog_price: String(data.default_per_dog_price),
        default_recurring_discount_pct: String(data.default_recurring_discount_pct),
        default_commission_pct: String(data.default_commission_pct),
        securement_fee: String(data.securement_fee),
        provider_display_label: data.provider_display_label,
        credit_label: data.credit_label,
        admin_portal_heading: data.admin_portal_heading ?? "{firstName}'s controls",
      });
      setUpdatedAt(data.updated_at || null);
      await refresh();
      toast.success("Saved — changes are live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/config/pricing", CANONICAL_DEFAULTS);
      setForm({
        default_per_dog_price: String(data.default_per_dog_price),
        default_recurring_discount_pct: String(data.default_recurring_discount_pct),
        default_commission_pct: String(data.default_commission_pct),
        securement_fee: String(data.securement_fee),
        provider_display_label: data.provider_display_label,
        credit_label: data.credit_label,
        admin_portal_heading: data.admin_portal_heading ?? "{firstName}'s controls",
      });
      setUpdatedAt(data.updated_at || null);
      setErrors({});
      await refresh();
      toast.success("Reset to PooPluckers defaults.");
      setResetOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="platform-settings-section">
      <Card
        className="rounded-3xl border-border bg-pp-surface p-8 shadow-none"
        data-testid="admin-platform-settings-card"
      >
        <div className="mb-6">
          <h2 className="font-heading text-2xl font-semibold text-pp-text">
            Platform Settings
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            These values apply to every new booking site-wide. Changes save immediately when you click Save.
          </p>
        </div>

        {!form ? (
          <p className="text-sm text-muted-foreground" data-testid="admin-loading">
            Loading current settings…
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-settings-form">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-sm font-semibold text-pp-text">{f.label}</Label>
                <div className="mt-2">
                  <PrefixedInput
                    field={f}
                    value={form[f.key]}
                    onChange={(v) => update(f.key, v)}
                    error={errors[f.key]}
                  />
                </div>
                {errors[f.key] ? (
                  <p
                    className="mt-1 text-xs font-semibold text-red-600"
                    data-testid={`${f.testid}-error`}
                  >
                    {errors[f.key]}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">{f.helper}</p>
                )}
              </div>
            ))}

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <Button
                type="submit"
                disabled={saving || hasErrors || !dirty}
                className="h-12 rounded-full bg-pp-orange px-8 text-pp-cta-text hover:bg-pp-orange-hover"
                data-testid="admin-save-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
              <button
                type="button"
                onClick={() => setResetOpen(true)}
                className="inline-flex items-center gap-1 text-sm text-pp-text underline-offset-2 hover:underline"
                data-testid="admin-reset-link"
              >
                <RotateCcw className="h-3 w-3" />
                Reset to defaults
              </button>
            </div>
          </form>
        )}
      </Card>

      {updatedAt && (
        <p
          className="mt-3 text-xs italic text-muted-foreground"
          data-testid="admin-last-updated"
        >
          Last updated: {updatedAt}
        </p>
      )}

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="rounded-3xl" data-testid="admin-reset-modal">
          <DialogHeader>
            <DialogTitle>Reset all settings to PooPluckers defaults?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will overwrite your current values:
            <br />
            $20 / dog · 10% recurring discount · 10% platform fee · $15 securement · &ldquo;PooPartner&rdquo; label.
          </p>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setResetOpen(false)}
              className="rounded-full"
              data-testid="admin-reset-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReset}
              disabled={saving}
              className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
              data-testid="admin-reset-confirm"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
